import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Tag,
  Switch,
  Carousel,
  Grid,
} from "antd";
import {
  CheckOutlined,
  StarFilled,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { getSubscriptions } from "../actionCreator/commonActionCreator";
import { useDispatch, useSelector } from "react-redux";
import { postMethod } from "../services/HttpServices";
import { formatCurrency } from "../utils/formatCurrency";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PricingPlans = ({ isCheckout, setStep }) => {
  const [active, setActive] = useState("INR");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated } = useContext(AuthContext);
  const packages = useSelector((state) => state.CommonReducer.subscriptions);
  const screens = useBreakpoint();

  useEffect(() => {
    const currencyParam = (searchParams.get("currency") || "").toUpperCase();
    if (currencyParam === "USD" || currencyParam === "INR") {
      setActive(currencyParam);
    }

    const cycleParam = (searchParams.get("cycle") || "").toLowerCase();
    if (cycleParam === "monthly" || cycleParam === "yearly") {
      setBillingCycle(cycleParam);
    }
  }, [searchParams]);

  const maxDiscountPercentage = React.useMemo(() => {
    if (!packages || packages.length === 0) return 0;
    let maxPercent = 0;
    packages.forEach((pkg) => {
      const planPrice = Number(pkg.planPrice) || 0;
      if (planPrice <= 0) return;

      const standardYearly = planPrice * 12;
      let savings = 0;

      if (
        pkg.yearlyBasePriceOverride &&
        Number(pkg.yearlyBasePriceOverride) > 0
      ) {
        savings = standardYearly - Number(pkg.yearlyBasePriceOverride);
      } else if (
        pkg.approvedYearlyDiscount &&
        Number(pkg.approvedYearlyDiscount) > 0
      ) {
        savings = Number(pkg.approvedYearlyDiscount);
      }

      if (savings > 0) {
        maxPercent = Math.max(maxPercent, (savings / standardYearly) * 100);
      }
    });

    return maxPercent > 0 ? Math.round(maxPercent) : 0;
  }, [packages]);

  useEffect(() => {
    dispatch(getSubscriptions());
  }, [dispatch]);

  const [calculatedPrices, setCalculatedPrices] = useState({});

  useEffect(() => {
    if (packages && packages.length > 0) {
      const fetchPrices = async () => {
        try {
          const results = await Promise.all(
            packages.map((pkg) =>
              postMethod("/api/public/pricing/calculate", {
                packageId: pkg.id,
                billingCycle: billingCycle.toUpperCase(),
                currency: active,
              }).then((res) => ({ id: pkg.id, ...res.data })),
            ),
          );
          const newPrices = {};
          results.forEach((r) => {
            newPrices[r.id] = r;
          });
          setCalculatedPrices(newPrices);
        } catch (error) {
          console.error("Failed to fetch dynamic prices:", error);
        }
      };
      fetchPrices();
    }
  }, [packages, billingCycle, active]);

  const yearlyDiscountLabel = React.useMemo(() => {
    if (billingCycle !== "yearly") return "";

    const pricingItems = Object.values(calculatedPrices || {});

    const promoPricing = pricingItems.find(
      (item) => Number(item?.baseCost) > 0 && Number(item?.promoDiscount) > 0,
    );
    if (promoPricing) {
      const baseCost = Number(promoPricing.baseCost);
      const promoDiscount = Number(promoPricing.promoDiscount);
      const percent = Math.round((promoDiscount / baseCost) * 100);

      if (percent > 0) return `${percent}% Off`;
    }

    const yearlyPricing = pricingItems.find(
      (item) => Number(item?.baseCost) > 0 && Number(item?.yearlyDiscount) > 0,
    );
    if (yearlyPricing) {
      const baseCost = Number(yearlyPricing.baseCost);
      const yearlyDiscount = Number(yearlyPricing.yearlyDiscount);
      const percent = Math.round((yearlyDiscount / baseCost) * 100);

      if (percent > 0) return `${percent}% Off`;
    }

    const discountedPricing = pricingItems.find(
      (item) =>
        Number(item?.baseCost) > 0 &&
        Number(item?.preTaxTotal) >= 0 &&
        Number(item?.baseCost) > Number(item?.preTaxTotal),
    );
    if (discountedPricing) {
      const baseCost = Number(discountedPricing.baseCost);
      const preTaxTotal = Number(discountedPricing.preTaxTotal);
      const percent = Math.round(((baseCost - preTaxTotal) / baseCost) * 100);

      if (percent > 0) return `${percent}% Off`;
    }

    return maxDiscountPercentage > 0
      ? `Up to ${maxDiscountPercentage}% Off`
      : "";
  }, [billingCycle, calculatedPrices, maxDiscountPercentage]);

  const getMonthlyPrice = (pkg, usdValue) => {
    if (active === "USD") {
      return usdValue;
    }
    return Number.parseInt(pkg?.planPrice, 10) || 0;
  };

  const usdMonthlyFallback = [39, 99, 199];
  const getUsdFallback = (pkg) => {
    const idx = (packages || []).findIndex((p) => p?.id === pkg?.id);
    return usdMonthlyFallback[idx] || 0;
  };

  const getAnnualPrice = (pkg, usdMonthlyBase) => {
    if (active === "USD") {
      return usdMonthlyBase * 12 * 0.8; // 20% flat fallback for USD
    }
    if (!pkg) return 0;

    const override = Number(pkg.yearlyBasePriceOverride);
    if (override > 0) return override;

    const baseCost = (Number(pkg.planPrice) || 0) * 12;
    const discount = Number(pkg.approvedYearlyDiscount) || 0;
    return baseCost - discount;
  };

  const formatPrice = (amount) => {
    const num = Number(amount);
    return formatCurrency(Number.isFinite(num) ? Math.round(num) : 0, active);
  };

  const getPlanPrice = (pkg, usdMonthly) => {
    const dynamic = calculatedPrices[pkg?.id];
    if (dynamic?.preTaxTotal != null) return Number(dynamic.preTaxTotal);
    return billingCycle === "yearly"
      ? getAnnualPrice(pkg, usdMonthly)
      : getMonthlyPrice(pkg, usdMonthly);
  };

  const displayPackages = React.useMemo(() => {
    return (packages || [])
      .filter((pkg) => {
        const price = getPlanPrice(pkg, getUsdFallback(pkg));
        return Number(price) > 0;
      })
      .slice(0, 3);
  }, [packages, calculatedPrices, billingCycle, active]);

  const buildFeatures = (pkg, index) => {
    const customFields = pkg?.customFieldLimit ?? 0;
    const leadLimit = pkg?.leadCreationLimit ?? 0;

    const leadCapture = [
      "Web-to-Lead Capture",
      `${customFields} Custom Fields (Branded Forms)`,
      `${leadLimit} Data-to-Lead Conversion/month`,
    ];
    if (index === 1) {
      leadCapture.push(
        "Basic Integrated Data Sources",
        "Basic Stages of Kanban Pipeline View",
      );
    } else if (index >= 2) {
      leadCapture.push(
        "Fully Access Integrated Data Sources",
        "Customizable/Filters Kanban Pipeline View",
      );
    }

    const reviewMgmt =
      index === 1
        ? ["Google Unified Review Management"]
        : index >= 2
          ? ["Google + OTA platforms Unified Review Management"]
          : [];

    const engagement =
      index === 0
        ? ["Direct Email Integration", "Unified Lead Timeline"]
        : ["Auto Lead Scoring & Tags"];

    const support =
      index === 0
        ? ["Email Support", "Basic Onboarding Support"]
        : index === 1
          ? ["Standard Onboarding Support"]
          : [
            "Priority Support",
            "API / Webhook Access",
            "Dedicated Onboarding Support",
          ];

    const sections = [
      { title: "Lead Generation & Capture", items: leadCapture },
      ...(reviewMgmt.length
        ? [{ title: "Review & Reputation Management", items: reviewMgmt }]
        : []),
      { title: "Engagement & Communication", items: engagement },
      { title: "Support & Add-ons", items: support },
    ];

    return sections;
  };

  const plans = displayPackages.map((pkg, index) => {
    const planName = pkg?.name || `Plan ${index + 1}`;
    const prevName =
      index > 0 ? displayPackages[index - 1]?.name || `Plan ${index}` : "";

    return {
      id: pkg?.id,
      name: planName,
      baseMonthly: getMonthlyPrice(pkg, getUsdFallback(pkg)),
      baseYearly: getAnnualPrice(pkg, getUsdFallback(pkg)),
      subTitle: index > 0 ? `Everything in ${prevName} +` : "",
      features: buildFeatures(pkg, index),
      highlight: index === 1, // most popular
    };
  });

  const cardsPerSlide = screens.lg ? 3 : screens.md ? 2 : 1;
  const chunkPlans = (items, size) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };
  const planSlides = React.useMemo(
    () => chunkPlans(plans, cardsPerSlide),
    [plans, cardsPerSlide],
  );

  return (
    <div
      style={{
        padding: isCheckout ? "40px 20px" : "80px 20px",
        maxWidth: "1200px",
        margin: "auto",
      }}
    >
      <div
        className="d-flex flex-column align-items-center"
        style={{ textAlign: "center", marginBottom: "60px" }}
      >
        {!isCheckout && (
          <>
            <h1 className="heading39px roboto-bold mb10 text-color-131415">
              Choose the Best Plan for Your Hospitality Business
            </h1>
            <p className="heading18px roboto-regular mb20 text-color-131415">
              Scalable, transparent pricing — no hidden fees, cancel anytime.
            </p>
          </>
        )}

        <Row className="tw-flex tw-items-center tw-justify-between tw-w-full">
          <Col xs={24} md={8}></Col>
          <Col
            xs={24}
            md={8}
            className="tw-flex tw-items-center tw-justify-center"
          >
            <div
              className="mt20 rounded-circle bg-muted"
              style={{ width: "fit-content" }}
            >
              <Button
                className={
                  active === "INR"
                    ? "theme-btn py-20"
                    : "bg-muted bg-muted-hover border-none py-20"
                }
                shape="round"
                style={{ minWidth: 80, maxWidth: 80 }}
                onClick={() => setActive("INR")}
              >
                ₹ INR
              </Button>
              <Button
                className={
                  active === "USD"
                    ? "theme-btn py-20"
                    : "bg-muted bg-muted-hover border-none py-20"
                }
                shape="round"
                style={{ minWidth: 80, maxWidth: 80 }}
                onClick={() => setActive("USD")}
              >
                $ USD
              </Button>
            </div>
          </Col>

          <Col
            xs={24}
            md={8}
            className="tw-flex tw-items-center gap-10 tw-justify-end"
          >
            <span className="tw-font-bold">Monthly</span>
            <Switch
              checked={billingCycle === "yearly"}
              onChange={(checked) =>
                setBillingCycle(checked ? "yearly" : "monthly")
              }
              style={{
                backgroundColor:
                  billingCycle === "yearly" ? "#5D44FF" : "#D9D9D9",
                margin: "0 8px",
              }}
            />
            <span className="tw-font-bold">
              Yearly{" "}
              {billingCycle === "yearly" && yearlyDiscountLabel ? (
                <span style={{ color: "#5D44FF" }}>({yearlyDiscountLabel})</span>
              ) : null}
            </span>
          </Col>
        </Row>
      </div>

      {plans.length > 3 ? (
        <Carousel dots>
          {planSlides.map((slide, slideIdx) => (
            <div key={`slide-${slideIdx}`}>
              <Row
                gutter={[24, 24]}
                className="d-flex flex-wrap justify-content-center"
              >
                {slide.map((plan, i) => {
                  let displayPrice = formatPrice(
                    billingCycle === "yearly"
                      ? plan.baseYearly
                      : plan.baseMonthly,
                  );
                  let dynamicInfo = calculatedPrices[plan?.id];

                  if (dynamicInfo) {
                    displayPrice = formatPrice(dynamicInfo.preTaxTotal);
                  }

                  const displayPer =
                    billingCycle === "yearly" ? "/year" : "/month";

                  return (
                    <Col
                      xs={24}
                      md={cardsPerSlide === 2 ? 12 : 8}
                      key={plan.id}
                      className={`${i === 1 ? "" : "pt30"} pb30 d-flex ${!plan.highlight && "reputation-manager"}`}
                    >
                      <Card
                        bordered
                        style={{
                          borderRadius: 12,
                          border: plan.highlight
                            ? "2px solid #5D44FF"
                            : "1px solid #f0f0f0",
                          background:
                            i === 1
                              ? "#F4F7FF 0% 0% no-repeat padding-box"
                              : "",
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          overflow: "visible",
                        }}
                        className={!plan.highlight && "review-card"}
                        bodyStyle={{
                          padding: plan.highlight ? "44px 24px 24px" : "24px",
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Highlight Tag */}
                        {plan.highlight && (
                          <Tag
                            color="#5D44FF"
                            style={{
                              position: "absolute",
                              top: 12,
                              left: "50%",
                              transform: "translateX(-50%)",
                              borderRadius: 20,
                              padding: "4px 12px",
                              fontWeight: "bold",
                              zIndex: 999,
                            }}
                          >
                            <StarFilled style={{ marginRight: 6 }} /> MOST
                            POPULAR <StarFilled style={{ marginRight: 6 }} />
                          </Tag>
                        )}

                        {/* Plan name */}
                        <Title
                          level={4}
                          style={{
                            marginBottom: 0,
                            marginTop: plan.highlight ? 24 : 0,
                            textAlign: "center",
                          }}
                        >
                          {plan.name}
                        </Title>

                        {/* Price */}
                        <div
                          className="d-flex flex-column align-items-center justify-content-center"
                          style={{ margin: "16px 0", textAlign: "center" }}
                        >
                          <div className="d-flex align-items-end justify-content-center">
                            <Title level={2} style={{ marginBottom: 0 }}>
                              {displayPrice}
                            </Title>
                            <Text type="secondary" className="mb5">
                              {displayPer}
                            </Text>
                          </div>
                          {dynamicInfo &&
                            dynamicInfo.baseCost > dynamicInfo.preTaxTotal && (
                              <Text type="secondary" delete className="mt5">
                                {formatPrice(dynamicInfo.baseCost)} {displayPer}
                              </Text>
                            )}
                          {dynamicInfo && dynamicInfo.appliedOfferName && (
                            <Tag
                              color="green"
                              className="mt10 fs-12 fw-bold"
                              style={{
                                borderRadius: "15px",
                                padding: "2px 10px",
                              }}
                            >
                              <StarFilled
                                style={{ fontSize: "10px", marginRight: "5px" }}
                              />
                              Offer: {dynamicInfo.appliedOfferName}
                            </Tag>
                          )}
                        </div>
                        <div
                          style={{ textAlign: "center", minHeight: "24px" }}
                          className="fw-semibold"
                        >
                          {plan?.subTitle}
                        </div>
                        {/* Features */}
                        <div style={{ marginBottom: 24, flex: 1 }}>
                          {plan.features.map((f, i) => (
                            <React.Fragment key={i}>
                              <div className="fw-semibold d-flex align-items-center gap-10 mt20">
                                <CheckCircleFilled /> {f.title}
                              </div>
                              <div>
                                <hr
                                  className="mt5 mb10"
                                  style={{ borderTop: "1px solid #1A3151" }}
                                />
                              </div>
                              {f?.items?.map((d) => (
                                <div style={{ marginBottom: 8 }}>
                                  <CheckOutlined style={{ color: "#399E72" }} />{" "}
                                  <span style={{ marginLeft: 8 }}>{d}</span>
                                </div>
                              ))}
                            </React.Fragment>
                          ))}
                        </div>

                        {/* Button */}
                        <Button
                          type={plan.highlight ? "primary" : "default"}
                          className={
                            plan.highlight ? "theme-btn" : "secondary-btn"
                          }
                          block
                          onClick={() => {
                            if (isCheckout) {
                              setStep(1);
                              searchParams.set("plan", String(plan?.id));
                              searchParams.set(
                                "cycle",
                                billingCycle.toUpperCase(),
                              );
                              searchParams.set("currency", active);
                              navigate(
                                `${location.pathname}?${searchParams.toString()}`,
                              );
                            } else {
                              navigate(
                                isAuthenticated
                                  ? `/checkout?plan=${plan?.id}&cycle=${billingCycle.toUpperCase()}&currency=${active}`
                                  : `/signup?type=subscribe&plan=${plan?.id}&cycle=${billingCycle.toUpperCase()}&currency=${active}`,
                              );
                            }
                          }}
                        >
                          Subscribe Now
                        </Button>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>
          ))}
        </Carousel>
      ) : (
        <Row
          gutter={[24, 24]}
          className="d-flex flex-wrap justify-content-center"
        >
          {plans.map((plan, i) => {
            let displayPrice = formatPrice(
              billingCycle === "yearly" ? plan.baseYearly : plan.baseMonthly,
            );
            let dynamicInfo = calculatedPrices[plan?.id];

            if (dynamicInfo) {
              displayPrice = formatPrice(dynamicInfo.preTaxTotal);
            }

            const displayPer = billingCycle === "yearly" ? "/year" : "/month";

            return (
              <Col
                xs={24}
                md={plans.length === 1 ? 12 : 8}
                key={plan.id}
                className={`${i === 1 ? "" : "pt30"} pb30 d-flex ${!plan.highlight && "reputation-manager"}`}
              >
                <Card
                  bordered
                  style={{
                    borderRadius: 12,
                    border: plan.highlight
                      ? "2px solid #5D44FF"
                      : "1px solid #f0f0f0",
                    background:
                      i === 1 ? "#F4F7FF 0% 0% no-repeat padding-box" : "",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "visible",
                  }}
                  className={!plan.highlight && "review-card"}
                  bodyStyle={{
                    padding: "24px",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Highlight Tag */}
                  {plan.highlight && (
                    <Tag
                      color="#5D44FF"
                      style={{
                        position: "absolute",
                        top: "-15px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        borderRadius: 20,
                        padding: "4px 12px",
                        fontWeight: "bold",
                        zIndex: 2,
                      }}
                    >
                      <StarFilled style={{ marginRight: 6 }} /> MOST POPULAR{" "}
                      <StarFilled style={{ marginRight: 6 }} />
                    </Tag>
                  )}

                  {/* Plan name */}
                  <Title
                    level={4}
                    style={{
                      marginBottom: 0,
                      marginTop: plan.highlight ? 24 : 0,
                      textAlign: "center",
                    }}
                  >
                    {plan.name}
                  </Title>

                  {/* Price */}
                  <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{ margin: "16px 0", textAlign: "center" }}
                  >
                    <div className="d-flex align-items-end justify-content-center">
                      <Title level={2} style={{ marginBottom: 0 }}>
                        {displayPrice}
                      </Title>
                      <Text type="secondary" className="mb5">
                        {displayPer}
                      </Text>
                    </div>
                    {dynamicInfo &&
                      dynamicInfo.baseCost > dynamicInfo.preTaxTotal && (
                        <Text type="secondary" delete className="mt5">
                          {formatPrice(dynamicInfo.baseCost)} {displayPer}
                        </Text>
                      )}
                    {dynamicInfo && dynamicInfo.appliedOfferName && (
                      <Tag
                        color="green"
                        className="mt10 fs-12 fw-bold"
                        style={{ borderRadius: "15px", padding: "2px 10px" }}
                      >
                        <StarFilled
                          style={{ fontSize: "10px", marginRight: "5px" }}
                        />
                        Offer: {dynamicInfo.appliedOfferName}
                      </Tag>
                    )}
                  </div>
                  <div
                    style={{ textAlign: "center", minHeight: "24px" }}
                    className="fw-semibold"
                  >
                    {plan?.subTitle}
                  </div>
                  {/* Features */}
                  <div style={{ marginBottom: 24, flex: 1 }}>
                    {plan.features.map((f, i) => (
                      <React.Fragment key={i}>
                        <div className="fw-semibold d-flex align-items-center gap-10 mt20">
                          <CheckCircleFilled /> {f.title}
                        </div>
                        <div>
                          <hr
                            className="mt5 mb10"
                            style={{ borderTop: "1px solid #1A3151" }}
                          />
                        </div>
                        {f?.items?.map((d) => (
                          <div style={{ marginBottom: 8 }}>
                            <CheckOutlined style={{ color: "#399E72" }} />{" "}
                            <span style={{ marginLeft: 8 }}>{d}</span>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Button */}
                  <Button
                    type={plan.highlight ? "primary" : "default"}
                    className={plan.highlight ? "theme-btn" : "secondary-btn"}
                    block
                    onClick={() => {
                      if (isCheckout) {
                        setStep(1);
                        searchParams.set("plan", String(plan?.id));
                        searchParams.set("cycle", billingCycle.toUpperCase());
                        searchParams.set("currency", active);
                        navigate(
                          `${location.pathname}?${searchParams.toString()}`,
                        );
                      } else {
                        navigate(
                          isAuthenticated
                            ? `/checkout?plan=${plan?.id}&cycle=${billingCycle.toUpperCase()}&currency=${active}`
                            : `/signup?type=subscribe&plan=${plan?.id}&cycle=${billingCycle.toUpperCase()}&currency=${active}`,
                        );
                      }
                    }}
                  >
                    Subscribe Now
                  </Button>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
      {!isCheckout && (
        <div className="d-flex align-items-center justify-content-center mt30">
          <Button
            type="primary"
            className="theme-btn"
            onClick={() => navigate("/pricing-details")}
          >
            View More Details
          </Button>
        </div>
      )}
    </div>
  );
};

export default PricingPlans;
